import React, { useState, useMemo } from "react";
import { createPortal } from "react-dom";
import styles from "./CommentBottomSheet.module.css";
import { Comment } from "./FeedItem";
import FeedCommentInput from "./FeedCommentInput";
import HeartBurstAnimation from "./HeartBurstAnimation";
import {
  getProfileImageWithFallback,
  handleImageError,
} from "../utils/imageUtils";

interface CommentBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  comments: Comment[];
  onSubmitComment: (comment: string, parentCommentId?: string) => void;
  onLikeComment?: (commentId: string) => void;
}

interface CommentWithReplies extends Comment {
  replies?: CommentWithReplies[];
  isReply?: boolean;
}

const CommentBottomSheet: React.FC<CommentBottomSheetProps> = ({
  isOpen,
  onClose,
  comments,
  onSubmitComment,
  onLikeComment,
}) => {
  const [likingComments, setLikingComments] = useState<{
    [id: string]: boolean;
  }>({});
  const [heartBurstStates, setHeartBurstStates] = useState<{
    [id: string]: boolean;
  }>({});
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyingToNickname, setReplyingToNickname] = useState<string>("");

  // 댓글을 계층 구조로 정리
  const organizedComments = useMemo(() => {
    const commentMap = new Map<string, CommentWithReplies>();
    const rootComments: CommentWithReplies[] = [];

    // 먼저 모든 댓글을 맵에 저장
    comments.forEach((comment) => {
      const commentWithReplies: CommentWithReplies = {
        ...comment,
        replies: [],
      };
      commentMap.set(comment.id, commentWithReplies);
    });

    // 댓글을 부모-자식 관계로 정리
    comments.forEach((comment) => {
      const commentWithReplies = commentMap.get(comment.id)!;

      // parentCommentId가 있는 경우 (답글인 경우)
      if ((comment as any).parentCommentId) {
        const parentComment = commentMap.get((comment as any).parentCommentId);
        if (parentComment) {
          commentWithReplies.isReply = true;
          parentComment.replies = parentComment.replies || [];
          parentComment.replies.push(commentWithReplies);
        } else {
          // 부모 댓글이 없으면 루트 댓글로 처리
          rootComments.push(commentWithReplies);
        }
      } else {
        // 루트 댓글
        rootComments.push(commentWithReplies);
      }
    });

    // 루트 댓글은 최신순으로 정렬 (이미 Firebase에서 정렬됨)
    // 각 댓글의 답글들은 오래된 순으로 정렬
    rootComments.forEach((comment) => {
      if (comment.replies && comment.replies.length > 0) {
        comment.replies.sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
      }
    });

    return rootComments;
  }, [comments]);

  const handleLike = async (id: string) => {
    if (likingComments[id]) return; // 이미 처리 중인 경우 무시

    if (onLikeComment) {
      try {
        setLikingComments((prev) => ({ ...prev, [id]: true }));

        // 댓글을 찾아서 좋아요 상태 확인
        const comment = comments.find((c) => c.id === id);
        const willBeLiked = !comment?.liked;

        // 좋아요를 누른 경우에만 애니메이션 표시
        if (willBeLiked) {
          setHeartBurstStates((prev) => ({ ...prev, [id]: true }));
        }

        await onLikeComment(id);
      } catch (error) {
        console.error("댓글 좋아요 처리 중 오류:", error);
        alert("좋아요 처리에 실패했습니다.");
      } finally {
        setLikingComments((prev) => ({ ...prev, [id]: false }));
      }
    }
  };

  const handleReply = (commentId: string, nickname: string) => {
    setReplyingTo(commentId);
    setReplyingToNickname(nickname);
  };

  // 최상위 부모 댓글 ID를 찾는 함수
  const findRootCommentId = (commentId: string): string => {
    const comment = comments.find((c) => c.id === commentId);
    if (!comment) return commentId;

    // parentCommentId가 있으면 재귀적으로 최상위 부모를 찾음
    if ((comment as any).parentCommentId) {
      return findRootCommentId((comment as any).parentCommentId);
    }

    // parentCommentId가 없으면 이것이 최상위 댓글
    return commentId;
  };

  const handleSubmitComment = (comment: string) => {
    onSubmitComment(comment, replyingTo || undefined);
    // 답글 상태와 포커싱 초기화
    setReplyingTo(null);
    setReplyingToNickname("");
  };

  const renderComment = (comment: CommentWithReplies, isReply = false) => {
    // 현재 답글을 달고 있는 댓글의 최상위 부모 댓글 ID
    const rootCommentId = replyingTo ? findRootCommentId(replyingTo) : null;

    return (
      <div key={comment.id}>
        <div
          className={`${styles.commentItem} ${
            isReply ? styles.replyItem : ""
          } ${rootCommentId === comment.id ? styles.focusedComment : ""}`}
        >
          <img
            src={getProfileImageWithFallback(comment.profileImage)}
            alt={comment.nickname}
            className={styles.profileImage}
            onError={handleImageError}
          />
          <div className={styles.commentContent}>
            <div className={styles.commentHeader}>
              <span className={styles.nickname}>{comment.nickname}</span>
            </div>
            <p className={styles.commentText}>{comment.content}</p>
            <div className={styles.commentMetaRow}>
              <span className={styles.time}>{comment.createdAt}</span>
              <span
                className={styles.replyBtn}
                onClick={() => handleReply(comment.id, comment.nickname)}
              >
                답글 달기
              </span>
            </div>
          </div>
          <div className={styles.commentLikeWrap}>
            <button
              className={`${styles.likeButton} ${
                comment.liked ? styles.liked : ""
              }`}
              onClick={() => handleLike(comment.id)}
              disabled={likingComments[comment.id]}
              aria-label="좋아요"
              style={{ position: "relative" }}
            >
              <div className={styles.commentLikeContainer}>
                <img
                  src={
                    comment.liked
                      ? `${process.env.PUBLIC_URL}/images/icons/feed-like-active.png`
                      : `${process.env.PUBLIC_URL}/images/icons/feed-like.png`
                  }
                  alt="좋아요"
                  className={styles.heartIcon}
                />
                {(comment.likes || 0) > 0 && (
                  <span className={styles.likeCount}>{comment.likes || 0}</span>
                )}
              </div>
              <HeartBurstAnimation
                isVisible={heartBurstStates[comment.id] || false}
                onAnimationEnd={() =>
                  setHeartBurstStates((prev) => ({
                    ...prev,
                    [comment.id]: false,
                  }))
                }
              />
            </button>
          </div>
        </div>

        {/* 답글들 렌더링 */}
        {comment.replies && comment.replies.length > 0 && (
          <div className={styles.repliesContainer}>
            {comment.replies.map((reply) => renderComment(reply, true))}
          </div>
        )}
      </div>
    );
  };

  const modalContent = (
    <>
      <div
        className={styles.overlay}
        style={{ display: isOpen ? "block" : "none" }}
        onClick={onClose}
      />
      <div
        className={`${styles.bottomSheet} ${isOpen ? styles.open : ""}`}
        aria-hidden={!isOpen}
      >
        <div className={styles.header}>
          <h3>댓글</h3>
        </div>
        <div className={styles.commentList}>
          {organizedComments.map((comment) => renderComment(comment))}
          {organizedComments.length === 0 && (
            <div className={styles.noComments}>
              <p>
                아직 댓글이 없습니다
                <br />첫 댓글을 남겨주세요
              </p>
            </div>
          )}
        </div>
        <div className={styles.commentForm}>
          {replyingTo && (
            <div className={styles.replyingTo}>
              <span className={styles.replyingToText}>
                {replyingToNickname.concat("에게")}
              </span>
              <button
                className={styles.cancelReply}
                onClick={() => {
                  setReplyingTo(null);
                  setReplyingToNickname("");
                }}
              >
                <img src="/images/icons/map-close.svg" alt="취소" />
              </button>
            </div>
          )}
          <FeedCommentInput
            onSubmit={handleSubmitComment}
            placeholder={
              replyingTo
                ? `${replyingToNickname}님에게 답글...`
                : "집사님의 댓글을 기다리고 있어요"
            }
          />
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
};

export default CommentBottomSheet;
